import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DiffuserIllustration from "@/components/DiffuserIllustration";
import { PageSEO, ProductSchema } from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Machine order: largest to smallest
const MACHINE_ORDER = ["Ikara Tower", "Ikara Table Top", "Ikara Scent Box", "Ikara Revitalised"];

// Machine use cases
const MACHINE_USE_CASES = {
  "Ikara Tower": "Ideal for large commercial spaces like hotel lobbies, retail flagships, and grand corporate foyers.",
  "Ikara Table Top": "Perfect for medium-sized spaces including reception areas, boutiques, and private offices.",
  "Ikara Scent Box": "Designed for HVAC integration in spas, wellness centres, and premium fitness studios.",
  "Ikara Revitalised": "Smart connectivity for personal use and smaller residential or office spaces."
};

// Machine illustration types
const MACHINE_ILLUSTRATIONS = {
  "Ikara Tower": "tower",
  "Ikara Table Top": "tabletop",
  "Ikara Scent Box": "scentbox",
  "Ikara Revitalised": "revitalised"
};

const SPACE_CATEGORIES = [
  "Fitness & Active Spaces",
  "Spa & Wellness",
  "Salons & Beauty",
  "Hotel & Hospitality",
  "Restaurants & Fine Dining",
  "Corporate & Office",
  "Retail & Boutiques",
  "Residential & Homes",
  "Lounges & Member Clubs"
];

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [oils, setOils] = useState([]);
  const [machines, setMachines] = useState([]);
  const [categories, setCategories] = useState(SPACE_CATEGORIES);
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || "all"
  );
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const [oilsRes, machinesRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/fragrance-oils`),
          axios.get(`${API}/diffuser-machines`),
          axios.get(`${API}/space-categories`),
        ]);
        setOils(oilsRes.data.oils);
        // Sort machines by MACHINE_ORDER
        const sortedMachines = machinesRes.data.machines.sort((a, b) => {
          return MACHINE_ORDER.indexOf(a.name) - MACHINE_ORDER.indexOf(b.name);
        });
        setMachines(sortedMachines);
        setCategories(categoriesRes.data.categories);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    const category = searchParams.get("category");
    if (category) {
      setSelectedCategory(category);
    }
  }, [searchParams]);

  const filteredOils = selectedCategory === "all"
    ? oils
    : oils.filter(oil => oil.categories.includes(selectedCategory));

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    if (value === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: value });
    }
  };

  // Sort machines in correct order
  const sortedMachines = [...machines].sort((a, b) => {
    return MACHINE_ORDER.indexOf(a.name) - MACHINE_ORDER.indexOf(b.name);
  });

  return (
    <div data-testid="products-page">
      <PageSEO page="products" />
      
      {/* Hero Section */}
      <section className="section-luxury pt-32 pb-16" data-testid="products-hero">
        <div className="container-luxury">
          <div className="max-w-4xl">
            <p className="text-overline mb-6">Our Collection</p>
            <h1 className="text-display mb-8">
              Artisanal Fragrances &<br />Precision Diffusers
            </h1>
            <div className="divider-luxury" />
          </div>
        </div>
      </section>

      {/* Products Tabs */}
      <section className="pb-24" data-testid="products-content">
        <div className="container-luxury">
          <Tabs defaultValue="fragrances" className="w-full">
            <TabsList 
              className="w-full justify-start bg-transparent border-b rounded-none p-0 h-auto mb-12"
              style={{ borderColor: 'var(--border)' }}
            >
              <TabsTrigger 
                value="fragrances" 
                className="tab-luxury rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 mr-12"
                data-testid="tab-fragrances"
              >
                Fragrance Oils
              </TabsTrigger>
              <TabsTrigger 
                value="machines" 
                className="tab-luxury rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0"
                data-testid="tab-machines"
              >
                Diffuser Machines
              </TabsTrigger>
            </TabsList>

            {/* Fragrance Oils Tab */}
            <TabsContent value="fragrances" data-testid="fragrances-content">
              {/* Category Filter - Horizontally scrollable on mobile */}
              <div className="mb-12">
                <h2 className="text-overline mb-6">Filter by Space</h2>
                <div className="category-filter-scroll">
                  <div className="category-filter-container">
                    <button
                      onClick={() => handleCategoryChange("all")}
                      className={`category-filter-btn ${selectedCategory === "all" ? "active" : ""}`}
                      data-testid="filter-all"
                    >
                      All Scents
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategoryChange(category)}
                        className={`category-filter-btn ${selectedCategory === category ? "active" : ""}`}
                        data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
                {selectedCategory !== "all" && (
                  <p className="mt-6 text-sm" style={{ color: 'var(--secondary-text)' }}>
                    Showing {filteredOils.length} scent{filteredOils.length !== 1 ? 's' : ''} for {selectedCategory}
                  </p>
                )}
              </div>

              {/* Oils List */}
              <div className="space-y-0">
                {filteredOils.map((oil, index) => (
                  <div 
                    key={`${oil.name}-${index}`}
                    className="product-item grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-center"
                    data-testid={`oil-${oil.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ProductSchema name={oil.name} notes={oil.notes} />
                    <div className="md:col-span-3">
                      <h3 className="font-heading text-2xl lg:text-3xl font-light">
                        {oil.name}
                      </h3>
                    </div>
                    <div className="md:col-span-6">
                      <p 
                        className="text-sm leading-relaxed" 
                        style={{ color: 'var(--secondary-text)' }}
                        title={`Ikara ${oil.name} fragrance oil — ${oil.notes}`}
                      >
                        {oil.notes}
                      </p>
                    </div>
                    <div className="md:col-span-3 md:text-right">
                      <Link 
                        to="/contact" 
                        className="inline-flex items-center gap-2 text-sm tracking-wider uppercase transition-opacity hover:opacity-70"
                        style={{ letterSpacing: '0.1em' }}
                        data-testid={`enquire-oil-${index}`}
                        aria-label={`Enquire about Ikara ${oil.name} fragrance oil`}
                      >
                        Enquire Now
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {filteredOils.length === 0 && (
                <div className="py-16 text-center">
                  <p className="text-subtitle">
                    No fragrances found for this category.
                  </p>
                </div>
              )}
            </TabsContent>

            {/* Diffuser Machines Tab */}
            <TabsContent value="machines" data-testid="machines-content">
              <h2 className="sr-only">Diffuser Machines Collection</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                {sortedMachines.map((machine, index) => (
                  <div 
                    key={machine.name}
                    className="machine-card p-8 lg:p-10"
                    style={{ 
                      backgroundColor: 'var(--surface)',
                      border: '1px solid var(--border)'
                    }}
                    data-testid={`machine-card-${index}`}
                  >
                    {/* Illustration */}
                    <div 
                      className="flex justify-center mb-8"
                      role="img"
                      aria-label={`Ikara ${machine.name} scent diffuser for commercial spaces`}
                    >
                      <DiffuserIllustration 
                        type={MACHINE_ILLUSTRATIONS[machine.name]}
                        className="w-32 h-32 md:w-40 md:h-40"
                      />
                    </div>
                    
                    <h3 className="font-heading text-2xl lg:text-3xl font-light mb-3 text-center">
                      {machine.name}
                    </h3>
                    <p className="text-sm text-center mb-6" style={{ color: 'var(--secondary-text)' }}>
                      {MACHINE_USE_CASES[machine.name]}
                    </p>
                    <div className="text-center">
                      <Link 
                        to="/contact" 
                        className="btn-luxury-outline inline-flex items-center gap-2"
                        data-testid={`machine-enquire-btn-${index}`}
                        aria-label={`Enquire about ${machine.name} diffuser`}
                      >
                        Enquire Now
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section-luxury"
        style={{ backgroundColor: 'var(--surface)' }}
        data-testid="products-cta"
      >
        <div className="container-luxury text-center">
          <p className="text-overline mb-6">Need Guidance?</p>
          <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl mb-8">
            Let Us Help You Choose
          </h2>
          <p className="text-subtitle max-w-2xl mx-auto mb-12">
            With 28 signature scents and 4 diffuser solutions, finding the 
            perfect combination for your space can be overwhelming. Our 
            experts are here to guide you through the selection process.
          </p>
          <Link 
            to="/contact" 
            className="btn-luxury inline-flex items-center gap-2"
            data-testid="products-contact-btn"
          >
            Schedule a Consultation
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
